// Payslip service - generate and manage employee payslips
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

export const payslipsService = {
    /**
     * Generate payslip for an employee
     */
    async generatePayslip(companyId, periodId, employeeId, payrollEntry, createdBy) {
        try {
            console.log('🔄 Generating payslip:', { companyId, periodId, employeeId });

            // Get period details
            const periodRef = doc(db, 'payrollPeriods', periodId);
            const periodDoc = await getDoc(periodRef);
            
            if (!periodDoc.exists()) {
                throw new Error('Payroll period not found');
            }
            
            const period = { id: periodDoc.id, ...periodDoc.data() };

            // Get employee details
            const { payrollService } = await import('./payroll');
            const employees = await payrollService.getEmployees(companyId);
            const employee = employees.find(emp => emp.id === employeeId);
            
            if (!employee) {
                throw new Error('Employee not found');
            }

            // Get company details
            const { companySettingsService } = await import('../system/company-settings');
            const companySettings = await companySettingsService.getCompanySettings(companyId);

            // Calculate payslip data
            const payslipData = {
                companyId,
                periodId,
                employeeId,
                employeeName: employee.name,
                employeeCode: employee.employeeId || employee.driverCode || `EMP${employee.id.slice(-4)}`,
                employeeRole: employee.role,
                employeeType: employee.type,
                
                // Period information
                periodYear: period.year,
                periodMonth: period.month,
                periodStartDate: period.startDate,
                periodEndDate: period.endDate,
                
                // Salary breakdown
                basicSalary: payrollEntry.salary || 0,
                commission: payrollEntry.commission || 0,
                bonuses: payrollEntry.bonus || 0,
                deductions: payrollEntry.deductions || 0,
                totalEarnings: (payrollEntry.salary || 0) + (payrollEntry.commission || 0) + (payrollEntry.bonus || 0),
                totalDeductions: payrollEntry.deductions || 0,
                netPay: payrollEntry.totalDue || 0,
                
                // Company information
                companyName: companySettings?.companyName || 'Company',
                companyAddress: companySettings?.address || '',
                
                // Status and metadata
                status: 'generated',
                paymentStatus: payrollEntry.paymentStatus || 'accrued',
                generatedBy: createdBy,
                generatedAt: serverTimestamp(),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            // Save payslip
            const payslipsRef = collection(db, 'payslips');
            const payslipRef = await addDoc(payslipsRef, payslipData);
            
            console.log('✅ Payslip generated:', payslipRef.id);
            return { id: payslipRef.id, ...payslipData };

        } catch (error) {
            console.error('❌ Error generating payslip:', error);
            throw error;
        }
    },

    /**
     * Get payslips for an employee
     */
    async getEmployeePayslips(companyId, employeeId) {
        try {
            const payslipsRef = collection(db, 'payslips');
            // Use simple query to avoid composite index requirement
            const q = query(
                payslipsRef,
                where('companyId', '==', companyId),
                where('employeeId', '==', employeeId)
            );
            
            const snapshot = await getDocs(q);
            const payslips = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            // Sort in memory to avoid composite index requirement
            return payslips.sort((a, b) => {
                if (a.periodYear !== b.periodYear) {
                    return b.periodYear - a.periodYear;
                }
                return b.periodMonth - a.periodMonth;
            });
        } catch (error) {
            console.error('Error getting employee payslips:', error);
            throw error;
        }
    },

    /**
     * Get payslips for a period
     */
    async getPeriodPayslips(companyId, periodId) {
        try {
            const payslipsRef = collection(db, 'payslips');
            const q = query(
                payslipsRef,
                where('companyId', '==', companyId),
                where('periodId', '==', periodId)
            );
            
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error getting period payslips:', error);
            throw error;
        }
    },

    /**
     * Update payslip payment status
     */
    async updatePayslipPaymentStatus(payslipId, paymentStatus, updatedBy) {
        try {
            const payslipRef = doc(db, 'payslips', payslipId);
            await updateDoc(payslipRef, {
                paymentStatus,
                updatedBy,
                updatedAt: serverTimestamp()
            });
            
            return true;
        } catch (error) {
            console.error('Error updating payslip payment status:', error);
            throw error;
        }
    },

    /**
     * Generate payslips for all employees in a period
     */
    async generatePeriodPayslips(companyId, periodId, createdBy) {
        try {
            console.log('🔄 Generating payslips for period:', periodId);

            // Get payroll entries for the period
            const { payrollService } = await import('./payroll');
            const payrollEntries = await payrollService.getPayrollEntries(companyId, periodId);
            
            const generatedPayslips = [];
            
            for (const entry of payrollEntries) {
                try {
                    const payslip = await this.generatePayslip(
                        companyId,
                        periodId,
                        entry.employeeId,
                        entry,
                        createdBy
                    );
                    generatedPayslips.push(payslip);
                } catch (error) {
                    console.error(`Failed to generate payslip for employee ${entry.employeeId}:`, error);
                }
            }
            
            console.log('✅ Generated payslips:', generatedPayslips.length);
            return generatedPayslips;

        } catch (error) {
            console.error('❌ Error generating period payslips:', error);
            throw error;
        }
    },

    /**
     * Get payment history for an employee
     */
    async getEmployeePaymentHistory(companyId, employeeId) {
        try {
            let payslips = [];
            let commissionPayments = [];
            
            // Get payslips (handle case where they might not exist)
            try {
                payslips = await this.getEmployeePayslips(companyId, employeeId);
            } catch (payslipError) {
                console.warn('Could not load payslips:', payslipError);
                payslips = [];
            }
            
            // Get commission payments (handle case where they might not exist)
            try {
                const commissionsRef = collection(db, 'commissionPayments');
                const commissionQuery = query(
                    commissionsRef,
                    where('employeeId', '==', employeeId)
                );
                const commissionSnapshot = await getDocs(commissionQuery);
                commissionPayments = commissionSnapshot.docs.map(doc => ({
                    id: doc.id,
                    type: 'commission',
                    ...doc.data()
                }));
            } catch (commissionError) {
                console.warn('Could not load commission payments:', commissionError);
                commissionPayments = [];
            }

            // If no payslips exist, try to get payroll entries directly
            if (payslips.length === 0) {
                try {
                    const { payrollService } = await import('./payroll');
                    const allPeriods = await payrollService.getPayrollPeriods(companyId);
                    
                    for (const period of allPeriods) {
                        try {
                            const entries = await payrollService.getPayrollEntries(companyId, period.id);
                            const employeeEntry = entries.find(entry => entry.employeeId === employeeId);
                            
                            if (employeeEntry) {
                                payslips.push({
                                    id: `entry-${employeeEntry.id}`,
                                    type: 'payroll',
                                    periodYear: period.year,
                                    periodMonth: period.month,
                                    employeeName: employeeEntry.employeeName,
                                    basicSalary: employeeEntry.salary || 0,
                                    commission: employeeEntry.commission || 0,
                                    netPay: employeeEntry.totalDue || 0,
                                    paymentStatus: employeeEntry.paymentStatus || 'accrued',
                                    generatedAt: employeeEntry.createdAt || new Date(),
                                    date: employeeEntry.createdAt?.toDate?.() || new Date(employeeEntry.createdAt) || new Date(),
                                    amount: employeeEntry.totalDue || 0
                                });
                            }
                        } catch (entryError) {
                            console.warn(`Could not load entries for period ${period.id}:`, entryError);
                        }
                    }
                } catch (periodError) {
                    console.warn('Could not load payroll periods:', periodError);
                }
            }

            // Combine and sort by date
            const allPayments = [
                ...payslips.map(payslip => ({
                    ...payslip,
                    type: payslip.type || 'payroll',
                    date: payslip.date || payslip.generatedAt?.toDate?.() || new Date(payslip.generatedAt) || new Date(),
                    amount: payslip.amount || payslip.netPay || 0
                })),
                ...commissionPayments.map(payment => ({
                    ...payment,
                    date: payment.paidDate ? new Date(payment.paidDate) : new Date(),
                    amount: payment.amount || 0
                }))
            ].sort((a, b) => new Date(b.date) - new Date(a.date));

            return allPayments;
        } catch (error) {
            console.error('Error getting employee payment history:', error);
            // Return empty array instead of throwing to prevent UI crashes
            return [];
        }
    }
};